import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Trans } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import { useLatest } from "react-use";

import { faHourglassStart } from "@fortawesome/pro-regular-svg-icons";
import { trackTestTimeout } from "../../amplitude";
import { formatTimeInterval } from "../../utils/format";
import { useTimeout } from "../../utils/timeout";
import { useFlashMessages } from "../flash-messages";
import { Icon } from "../icon";

const ALERT_THRESHOLD = {
  orange: 120000,
  red: 60000,
};

interface TimerProps {
  endsAt: Date | null;
  testDuration: number;
  onSubmit: () => void;
  submitting: boolean;
  jobOpeningSlug: string;
}

export function Timer(props: TimerProps) {
  const { endsAt, testDuration, submitting, jobOpeningSlug } = props;
  const onSubmit = useLatest(props.onSubmit);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const timer = useRef<number | null>(null);
  const { addMessage } = useFlashMessages();
  const interval =
    endsAt != null ? Math.max(endsAt.getTime() - currentTime, 0) : null;
  const alert = interval != null ? interval < ALERT_THRESHOLD.orange : false;
  const alertType =
    interval != null
      ? interval < ALERT_THRESHOLD.orange && interval > ALERT_THRESHOLD.red
        ? "orange"
        : interval < ALERT_THRESHOLD.red
          ? "red"
          : "green"
      : "green";
  const timeLimit = testDuration * 1000;

  /**
   * Handle timer
   */
  useEffect(() => {
    timer.current = window.setInterval(() => setCurrentTime(Date.now), 200);
    return () => {
      if (timer.current !== null) {
        window.clearInterval(timer.current);
      }
    };
  }, []);

  /**
   * Handle auto submission when the time is out
   */
  useTimeout(endsAt && endsAt.getTime(), () => {
    if (submitting) return;

    addMessage({ type: "timeout" });
    onSubmit.current();

    trackTestTimeout(jobOpeningSlug, {
      "ends at": endsAt,
      now: Date.now(),
      currentTime: currentTime,
    });
  });

  return (
    <Container role="timer" aria-label="remaining">
      <IconWrapper alertType={alertType} alert={alert}>
        <IconContent alertType={alertType} alert={alert}>
          <Icon icon={faHourglassStart} color="white" fontSize="12px" />
        </IconContent>
      </IconWrapper>
      <Text>
        <Time>
          {interval != null
            ? formatTimeInterval(interval)
            : timeLimit
              ? formatTimeInterval(timeLimit)
              : "–:––"}
        </Time>
        <Trans>remaining</Trans>
      </Text>
    </Container>
  );
}

const scale = keyframes`
  from,
  to {
    transform: scale(1);
  }

  10%,
  30%,
  50%,
  70%,
  90% {
    transform: scale(.9);
  }

  20%,
  40%,
  60%,
  80% {
    transform: scale(1.05);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const IconWrapper = styled.div<{
  alertType: "green" | "orange" | "red";
  alert: boolean;
}>`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  &:before {
    content: "";
    display: block;
    width: 100%;
    height: 40px;
    position: absolute;
    z-index: -1;
    border-radius: 50%;
    background: ${(props) =>
      props.alertType === "green"
        ? props.theme.colors.green[10]
        : props.alertType === "orange"
          ? props.theme.colors.orange[10]
          : props.theme.colors.red[10]};
    animation: ${scale} 10.5s linear infinite;
  }
`;

const IconContent = styled.div<{
  alertType: "green" | "orange" | "red";
  alert: boolean;
}>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(props) =>
    props.alertType === "green"
      ? props.theme.colors.green[100]
      : props.alertType === "orange"
        ? props.theme.colors.orange[100]
        : props.theme.colors.red[100]};
`;

const Text = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 700;
  font-size: 16px;
  color: ${(props) => props.theme.colors.purple[100]};
  margin-left: 6px;
  min-width: 130px;
`;

const Time = styled.div`
  margin-right: 4px;
  min-width: 50px;
  text-align: right;
`;
